import { useId, useRef, useState, type FormEvent } from 'react';
import { CircleAlert } from 'lucide-react';
import type { ApiError } from '../api/client';
import { codeOf } from '../api/errors';
import { useGrantMutation } from '../api/queries';
import { REASON_TEXT_MAX, REASONS, type ReasonCode } from '../api/types';

/**
 * 选查看理由（console/docs/specs/问题诊断查看权限.md）：管理员以外的人看某台设备的对话、录音、记忆、日记、人脸与声纹之前，
 * sidecar 回 403 `reason_required`，这里就在**原来放内容的位置**显示这张小表单，不跳中间页、不弹对话框。
 * 五个理由单选，选「其他」才出现必填的说明；登记成功后，停在这一步的查询全部重取（api/queries.ts 的 useGrantMutation）。
 *
 * 结构照样式包的表单（DESIGN.md §3.4、§4.3、§4.4）：带说明的单选 `.options`，字段 `.field`，底栏 `.form-f`。
 * `inCard`：放在已有卡片里（对话那张卡的工具条下面）就不再自己套一张卡，免得框套框。
 * 设备的 SN 取自 403 的 detail（sidecar 按读者投影：研发、客服是真 SN，履约、员工是假名），不用调用方另传。
 */
export function ReasonPanel({ error, what, inCard = false }: { error: ApiError; what: string; inCard?: boolean }) {
  const sn = typeof error.data?.sn === 'string' ? error.data.sn : '';
  const grant = useGrantMutation();
  const [code, setCode] = useState<ReasonCode | null>(null);
  const [text, setText] = useState('');
  // 「必填没填」在提交时才报（DESIGN.md §3.4）；提交失败一次后边输边校
  const [tried, setTried] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const id = useId();

  const noCode = code === null;
  const noText = code === 'other' && !text.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (noCode) return firstRef.current?.focus();
    if (noText) return textRef.current?.focus();
    grant.mutate({ sn, reason_code: code, ...(code === 'other' ? { reason_text: text.trim() } : {}) });
  };

  const title = <h2 id={`${id}-t`}>先选查看理由</h2>;
  const body = (
    <div className="card-b form">
      {inCard && (
        <div className="sec-h">
          <h3 id={`${id}-t`}>先选查看理由</h3>
        </div>
      )}
      <p className="fs-13">选一次，今天（北京时间）再看这台设备不用再选。每次查看都会记进「查看记录」，全员可见。</p>
      <div className="field">
        {/* 可见的标题已经是「先选查看理由」，这组单选的名字只给读屏 */}
        <span className="sr-only" id={`${id}-l`}>查看理由</span>
        <div
          className="options"
          role="radiogroup"
          aria-labelledby={`${id}-l`}
          aria-describedby={tried && noCode ? `${id}-ce` : undefined}
        >
          {REASONS.map((r, i) => (
            <label key={r.code} className="option">
              <input
                ref={i === 0 ? firstRef : undefined}
                type="radio"
                name={`${id}-reason`}
                value={r.code}
                checked={code === r.code}
                onChange={() => setCode(r.code)}
              />
              <span className="t">{r.hint ? `${r.label}（${r.hint}）` : r.label}</span>
            </label>
          ))}
        </div>
        {tried && noCode && (
          <p className="err" id={`${id}-ce`}>
            <CircleAlert className="ico" aria-hidden="true" />
            先选一个理由
          </p>
        )}
      </div>
      {code === 'other' && (
        <div className="field">
          <label htmlFor={`${id}-x`}>写明原因</label>
          <textarea
            ref={textRef}
            id={`${id}-x`}
            className="textarea"
            rows={2}
            maxLength={REASON_TEXT_MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-invalid={tried && noText ? true : undefined}
            aria-describedby={tried && noText ? `${id}-xe` : `${id}-xh`}
          />
          {tried && noText ? (
            <p className="err" id={`${id}-xe`}>
              <CircleAlert className="ico" aria-hidden="true" />
              选「其他」要写明为什么要看，最多 {REASON_TEXT_MAX} 字
            </p>
          ) : (
            <p className="hint" id={`${id}-xh`}>
              最多 {REASON_TEXT_MAX} 字，查看记录里全员可见。
            </p>
          )}
        </div>
      )}
      {grant.isError && (
        <p className="err" role="alert">
          <CircleAlert className="ico" aria-hidden="true" />
          {grantErrorText(grant.error, what)}
        </p>
      )}
    </div>
  );
  const foot = (
    <div className="form-f">
      <button type="submit" className="btn btn-primary" disabled={grant.isPending}>
        {grant.isPending ? '正在登记…' : '登记并查看'}
      </button>
    </div>
  );

  const form = (
    <form onSubmit={submit} noValidate aria-labelledby={`${id}-t`}>
      {!inCard && <div className="card-h">{title}</div>}
      {body}
      {foot}
    </form>
  );
  return inCard ? form : <section className="card">{form}</section>;
}

function grantErrorText(e: unknown, what: string): string {
  switch (codeOf(e)) {
    case 'reason_text_required':
      return '选「其他」要写明为什么要看。';
    case 'reason_text_too_long':
      return `原因最多 ${REASON_TEXT_MAX} 字，删短一点再登记。`;
    case 'invalid_reason':
      return '这个理由问题诊断不认识：页面可能刚更新过，刷新后再选一次。';
    case 'not_found':
      return '找不到这台设备，没能登记。确认地址里的 SN 对不对。';
    case 'forbidden':
      return '你的账号不能登记查看理由。需要的话找管理员。';
    case 'access_log_unavailable':
      return `查看记录写不进去，没能登记，${what}也就打不开。稍后重试；一直这样就报给研发。`;
    case 'network_offline':
      return '网络断开了，没能登记。恢复后再点一次。';
    default:
      return '没能登记：数据接口报错或连不上。重试一次；还不行，查 sidecar 日志里 /api/access/grants 的报错。';
  }
}
